import React from 'react'
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts'
import { formatNumber } from '~/utils/helpers'
import { WidgetCard, WidgetTitle } from '../../style'
import {
  MilchpreisContainer,
  MilchpreisLeft,
  PriceHeader,
  TotalPrice,
  PriceUnit,
  BreakdownItem,
  BreakdownValue,
  BreakdownLabel
} from './style'
import { useMilkMoney } from '../../MilkMoneyContext'
import { MilkPriceWidgetSkeleton } from '../../Skeletons'
import { useTranslation } from 'react-i18next'
import InfoPopup from '~/components/InfoPopup'

const SEGMENT_COLORS = ['#1FA3C8', '#4EC1C9', '#0E3A5D', '#4FAE6E', '#2F7F7A', '#B7D36A', '#FF9800', '#9C27B0']

const MilkPriceWidget: React.FC = () => {
  const { t } = useTranslation()
  const { milkMoneyData, isLoading } = useMilkMoney()

  if (isLoading) return <MilkPriceWidgetSkeleton />

  const factors = milkMoneyData?.factors ?? []

  if (!factors.length) {
    return (
      <WidgetCard>
        <WidgetTitle>{t('My milk price')}</WidgetTitle>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#999', fontSize: '14px' }}>
          {t('No data available')}
        </div>
      </WidgetCard>
    )
  }

  const total = factors.reduce((sum, f) => sum + parseFloat(f.value || '0'), 0)

  const segments = factors.map((f, i) => ({
    key: f.name,
    label: f.name,
    color: SEGMENT_COLORS[i % SEGMENT_COLORS.length]
  }))

  const chartEntry: Record<string, any> = { month: '' }
  factors.forEach((f) => {
    chartEntry[f.name] = parseFloat(f.value || '0')
  })

  const domainMax = Math.ceil(total * 1.1) || 100

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#333' }}>{label}</p>
          {payload.map((entry: any, index: number) => {
            const segment = segments.find((s) => s.key === entry.dataKey)
            return (
              <p key={index} style={{ margin: '4px 0', color: entry.fill, fontSize: '12px' }}>
                {segment?.label}
              </p>
            )
          })}
        </div>
      )
    }
    return null
  }

  return (
    <WidgetCard>
      <div style={{ display: 'flex', alignItems: 'baseline', gap:10 }}>
        <WidgetTitle>{t('My milk price')}</WidgetTitle>
        <div>{t('in development')}</div>
        <InfoPopup
          content={t('Calculation: Payout amount / quantity delivered in kg')}
          styleIcon={{ fontSize: '1.3em' }}
          style={{ minWidth: 400 }}
       />
      </div>
      <MilchpreisContainer>
        <MilchpreisLeft>
          <PriceHeader>
            <TotalPrice>{formatNumber(milkMoneyData?.price || 0, 2)}</TotalPrice>
            <PriceUnit>Rp./kg</PriceUnit>
          </PriceHeader>
          {factors.map((f, index) => {
            const value = parseFloat(f.value || '0')
            return (
              <BreakdownItem key={index}>
                <BreakdownValue isNegative={value < 0}>
                  {value >= 0 ? '+' : ''}
                </BreakdownValue>
                <BreakdownLabel>{f.factorName || f.name}</BreakdownLabel>
              </BreakdownItem>
            )
          })}
        </MilchpreisLeft>
        <ResponsiveContainer width='100%' height={250}>
          <BarChart
            data={[chartEntry]}
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            barSize={60}
          >
            <YAxis
              domain={[0, domainMax]}
              tickFormatter={(value) => formatNumber(value, 0)}
              tick={{ fontSize: 11, fill: '#666' }}
            />
            <XAxis dataKey='month' />
            <Tooltip
              content={<CustomTooltip />}
              wrapperStyle={{ zIndex: 9999 }}
              allowEscapeViewBox={{ x: true, y: true }}
            />
            {segments.map((segment) => (
              <Bar
                key={segment.key}
                dataKey={segment.key}
                stackId='price'
                fill={segment.color}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </MilchpreisContainer>
    </WidgetCard>
  )
}

export default MilkPriceWidget
